"use client";
import { findDivisionById } from "@gettactic/components/src/form/OfficeSelectorInput/SelectOffices";
import { useAuthenticated, useDivisionsMenuQuery } from "@gettactic/hooks";
import React from "react";
import { useUrlQuerySync } from "@/features/dashboard/hooks/useUrlQuerySync";
import { useMap } from "@/features/map/MapProvider";
import { useRouter } from "@/lib/useRouter";

export const useMapPageUrlSync = (enabled = true) => {
  const router = useRouter();

  const {
    userContext: { currentOffice }
  } = useAuthenticated();

  const {
    floors,
    floor,
    changeStartDate,
    changeEndTime,
    startDate,
    endDate,
    timeMode,
    setTimeMode,
    setLocateResourceId,
    setFloor,
    filterQuery,
    setFilterQuery,
    defaultTab,
    setSection,
    viewingDivision,
    setViewingDivision
  } = useMap();

  // Campus (division) maps restore from a shared URL: the division tree
  // loads independently of the floors gate below, so a divisionId seen at
  // load time is held in state until the tree is available to resolve it
  // (state, not a ref, so the effect fires regardless of which arrives
  // first).
  const { data: divisionsMenu } = useDivisionsMenuQuery();
  const [pendingDivisionId, setPendingDivisionId] = React.useState<
    string | null
  >(null);
  React.useEffect(() => {
    if (!pendingDivisionId || !divisionsMenu?.tree) return;
    const division = findDivisionById(divisionsMenu.tree, pendingDivisionId);
    if (division?.map_data) {
      setViewingDivision(division);
    }
    setPendingDivisionId(null);
  }, [pendingDivisionId, divisionsMenu?.tree, setViewingDivision]);

  const onFloorChange = (floorId?: string) => {
    if (!floorId) return;
    const foundFloor = floors?.find((floor) => floor.id === floorId);
    if (foundFloor) setFloor(foundFloor);
  };

  useUrlQuerySync(
    {
      date: startDate,
      // Campus view: the division is the shareable location — office and
      // floor are irrelevant noise, so they drop out of the URL.
      officeId: viewingDivision ? undefined : currentOffice.id,
      floorId: viewingDivision ? undefined : floor?.id,
      divisionId: viewingDivision?.id,
      startTime: startDate,
      endTime: endDate,
      timeMode,
      searchText: filterQuery || undefined, // Don't pass empty string to avoid it being converted to 0
      section: defaultTab
    },
    (paramsFromUrl) => {
      // Support old URL format with hash
      const [, hash] = (router.asPath as string).split("#");
      const parsedHash = new URLSearchParams(hash);
      const resourceId = parsedHash.get("resource") || null;
      const floorId = parsedHash.get("floorId") || null;
      const date =
        parsedHash.get("startTime") || parsedHash.get("dateTime") || null;
      const endDate = parsedHash.get("endTime") || null;

      const urlFloorId = floorId || paramsFromUrl.floorId;
      const urlResourceId =
        resourceId || (paramsFromUrl.resource as string | undefined);

      // Set floor from URL if it exists and is different from current floor
      // Also handle the case where floor is initially null/undefined
      if (urlFloorId && floors && floors.length > 0) {
        const floorIdStr = String(urlFloorId);
        if (floorIdStr !== floor?.id) {
          onFloorChange(floorIdStr);
        }
      }

      if (paramsFromUrl.divisionId) {
        // Resolved by the effect above once the divisions tree loads.
        setPendingDivisionId(String(paramsFromUrl.divisionId));
      }

      if (urlResourceId) {
        // Use locateResourceId for URL-based navigation
        // This triggers the zoom and highlight flow which handles resource selection
        setLocateResourceId(urlResourceId);
      }

      if (
        paramsFromUrl.searchText &&
        paramsFromUrl.searchText !== filterQuery
      ) {
        setFilterQuery(String(paramsFromUrl.searchText));
      }

      if (paramsFromUrl.section && paramsFromUrl.section !== defaultTab) {
        setSection(paramsFromUrl.section as any);
      }
    },
    enabled && router.isReady && (floors?.length || 0) > 0,
    false,
    true
  );
};
